import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUI } from '../../contexts/UIContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../providers/TranslationProvider';
import { SCREENS } from '../../utils/constants';
import { ArrowLeft, Mail, Phone, ChevronRight, AlertCircle, CheckCircle } from 'lucide-react';
import LetterGlitch from '../../components/LetterGlitch';
import Logo from '../../icons/logo.svg';

const ForgotPasswordScreen = () => {
  const { navigateToScreen } = useUI();
  const { resetPassword, loading: authLoading } = useAuth(); // Assuming resetPassword exists
  const { t } = useTranslation();

  // States
  const [method, setMethod] = useState('email'); // 'email' or 'phone'
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [localError, setLocalError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Theme state
  const [isDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const handleReset = async (e) => {
    e.preventDefault();
    setLocalError('');
    setSuccess(false);

    const identifier = method === 'email' ? email : phone;

    if (!identifier) {
      setLocalError(t('forgot.error.empty', 'Please enter your detail'));
      return;
    }

    setLoading(true);

    try {
      // Simulate API call or use actual auth function
      // await resetPassword(identifier, method); 
      await new Promise(resolve => setTimeout(resolve, 1500)); // Mock delay
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setLocalError(err.message || t('forgot.error.failed', 'Failed to send reset link.'));
    } finally {
      setLoading(false);
    }
  };

  // Animation Variants
  const containerVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.3 } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut", delay: 0.2 } }
  };

  const bgClass = isDarkMode ? 'bg-slate-900' : 'bg-slate-50';
  const textClass = isDarkMode ? 'text-white' : 'text-slate-900';
  const inputBg = isDarkMode ? 'bg-slate-800/50 border-slate-700 text-white placeholder-slate-500' : 'bg-white border-blue-100 text-slate-800 placeholder-slate-400';
  const cardBg = isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-white/80 border-white';

  return (
    <motion.div
      className={`relative w-full h-screen overflow-hidden flex flex-col items-center justify-center ${bgClass} ${textClass}`}
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Dynamic Background */}
      <div className={`absolute inset-0 transition-colors duration-700 ${isDarkMode ? 'bg-slate-950' : 'bg-gradient-to-br from-blue-50 to-white'}`}>
        {isDarkMode && (
          <div className="absolute inset-0 opacity-30 pointer-events-none">
            <LetterGlitch
              glitchColors={['#1e40af', '#3b82f6', '#60a5fa']}
              glitchSpeed={50}
              centerVignette={true}
              outerVignette={false}
              smooth={true}
            />
          </div>
        )}
      </div>

      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        onClick={() => navigateToScreen(SCREENS.LOGIN)}
        className="absolute top-6 left-6 z-20 p-2 rounded-full hover:bg-white/10 transition-colors"
        aria-label="Back"
      >
        <ArrowLeft size={24} className={isDarkMode ? 'text-white' : 'text-slate-800'} />
      </motion.button>

      {/* Main Content */}
      <motion.div
        className={`relative z-10 w-full max-w-md p-8 rounded-3xl backdrop-blur-xl border shadow-2xl ${cardBg}`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="flex flex-col items-center mb-8">
          <div className={`w-16 h-16 mb-4 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-slate-800 shadow-lg shadow-blue-900/20' : 'bg-white shadow-xl shadow-blue-100'}`}>
            <img src={Logo} alt="IDApps" className="w-10 h-10 object-contain" />
          </div>
          <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-600">
            {t('forgot.title', 'Reset Password')}
          </h2>
          <p className={`text-sm mt-2 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {t('forgot.subtitle', 'Enter your email or phone to receive a reset link')}
          </p>
        </div>

        {!success ? (
          <form onSubmit={handleReset} className="space-y-6">
            {/* Method Toggle */}
            <div className={`flex p-1 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-blue-50'}`}>
              <button
                type="button"
                onClick={() => { setMethod('email'); setLocalError(''); }}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${method === 'email'
                    ? (isDarkMode ? 'bg-slate-700 text-white shadow' : 'bg-white text-blue-600 shadow')
                    : (isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-blue-600')
                  }`}
              >
                Email
              </button>
              <button
                type="button"
                onClick={() => { setMethod('phone'); setLocalError(''); }}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${method === 'phone'
                    ? (isDarkMode ? 'bg-slate-700 text-white shadow' : 'bg-white text-blue-600 shadow')
                    : (isDarkMode ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-blue-600')
                  }`}
              >
                Phone
              </button>
            </div>

            {/* Input Fields */}
            <div className="space-y-1">
              {method === 'email' ? (
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full px-5 py-4 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all border ${inputBg}`}
                    placeholder="name@example.com"
                  />
                  <Mail size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 opacity-70" />
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className={`w-full px-5 py-4 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all border ${inputBg}`}
                    placeholder="+1 (555) 000-0000"
                  />
                  <Phone size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 opacity-70" />
                </div>
              )}
            </div>

            {/* Error Message */}
            <AnimatePresence>
              {localError && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm"
                >
                  <AlertCircle size={16} />
                  <span>{localError}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Submit Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={loading}
              type="submit"
              className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg relative overflow-hidden group ${isDarkMode
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-blue-900/30'
                  : 'bg-blue-600 text-white shadow-blue-200'
                }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('forgot.sending', 'Sending...')}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  {t('forgot.submit', 'Send Reset Link')}
                  <ChevronRight size={20} />
                </span>
              )}
            </motion.button>
          </form>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-4"
          >
            <div className="w-20 h-20 mx-auto bg-green-500/10 rounded-full flex items-center justify-center mb-6">
              <CheckCircle size={40} className="text-green-500" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('forgot.success.title', 'Check your inbox')}</h3>
            <p className={`text-sm mb-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {t('forgot.success.msg', `We have sent a password reset link to ${method === 'email' ? email : phone}`)}
            </p>

            <button
              onClick={() => navigateToScreen(SCREENS.LOGIN)}
              className={`w-full py-3 rounded-xl font-semibold border ${isDarkMode
                  ? 'border-slate-700 hover:bg-slate-800 text-white'
                  : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                }`}
            >
              {t('forgot.back', 'Back to Login')}
            </button>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default ForgotPasswordScreen;